import type { Store } from "@/common/storeUtils";
import type { ContentTypeProps, SearchProps } from "@/components/search/fullSearchStoreTypes";
import type { GaVueComponent } from "@/common/vueUtils";
import { GaButton } from "@/components/general/GaButton";
import ChannelCardMini from "@utils/vue-migration/component/channel/ChannelCardMini.vue";
import CustomPageCardMini from "@utils/vue-migration/component/custompage/CustomPageCardMini.vue";
import { ContentCard } from "@/components/cards/ContentCard";
import type { IxStatusHandler } from "@/common/ixStatusUtils";
import type { FullSearchContext } from "@generated-widgets/widgets/FullSearch";

function loadMore(page: Store<number>, search: () => void): void {
  page.set(page.get() + 1);
  search();
}

function countNextPage(totalCount: Store<bigint>, itemCount: Store<number>, pageSize: number): number {
  const countRemaining = Number(totalCount.get()) - itemCount.get();
  return countRemaining > pageSize ? pageSize : countRemaining;
}

export function ContentTypeChildren(props: { contentType: Store<ContentTypeProps>; search: () => void; searchProps: Store<SearchProps>; ctx: FullSearchContext; bookmarkHandler: IxStatusHandler }): GaVueComponent {
  const contentTypeProps = props.contentType.unpackUnion();
  const selected = props.searchProps.sub("selectedContentType").get() === props.contentType.get().kind;
  const category = contentTypeProps.store.get();
  const amountToShow = selected ? category.items.length : contentTypeProps.store.get().amountToShow;
  const standardColumns = "row row-cols-1 row-cols-md-2 g-3 ";
  const bigColumn = standardColumns + (selected ? "row-cols-lg-3" : "");
  const smallColumn = standardColumns + (selected ? "row-cols-lg-4" : "row-cols-lg-3");
  switch (contentTypeProps.kind) {
    case "SUMMARY":
      return (
        <>
          <div class={bigColumn}>
            {contentTypeProps.store
              .get()
              .items.slice(0, amountToShow)
              .map((result, i) => {
                const params = new URLSearchParams(props.searchProps.sub("params").get() as URLSearchParams);
                params.append("o_p", String(i));
                return (
                  <div class="col" key={i}>
                    <ContentCard content={result} layout="horizontal" additionalParams={params} bookmarkHandler={props.bookmarkHandler} ctx={props.ctx} />
                  </div>
                );
              })}
          </div>
          {props.contentType.sub("paging").sub("totalCount").get() > contentTypeProps.store.sub("items").sub("length").get() && selected ? (
            <div class="d-flex justify-content-center mt-3">
              <GaButton onClick={() => loadMore(props.contentType.sub("paging").sub("page"), props.search)}>
                {props.searchProps.sub("status").get() === "LOADMORE" ? (
                  <div class="spinner-border spinner-border-sm" />
                ) : (
                  props.ctx.t("browse:explore.loadMore", [String(countNextPage(props.contentType.sub("paging").sub("totalCount"), contentTypeProps.store.sub("items").sub("length"), 12))])
                )}
              </GaButton>
            </div>
          ) : null}
        </>
      );
    case "CHANNEL":
      return (
        <>
          <div class={bigColumn}>
            {contentTypeProps.store
              .get()
              .items.slice(0, amountToShow)
              .map((result, i) => {
                const params = new URLSearchParams(props.searchProps.sub("params").get() as URLSearchParams);
                params.append("o_p", String(i));
                return (
                  <div class="col" key={i}>
                    <ChannelCardMini channel={{ ...result, visibleLanguages: [...result.visibleLanguages] }} params={params} />
                  </div>
                );
              })}
          </div>
          {props.contentType.sub("paging").sub("totalCount").get() > contentTypeProps.store.sub("items").sub("length").get() && selected ? (
            <div class="d-flex justify-content-center mt-3">
              <GaButton onClick={() => loadMore(props.contentType.sub("paging").sub("page"), props.search)}>
                {props.searchProps.sub("status").get() === "LOADMORE" ? (
                  <div class="spinner-border spinner-border-sm" />
                ) : (
                  props.ctx.t("browse:explore.loadMore", [String(countNextPage(props.contentType.sub("paging").sub("totalCount"), contentTypeProps.store.sub("items").sub("length"), 9))])
                )}
              </GaButton>
            </div>
          ) : null}
        </>
      );
    case "CUSTOMPAGE":
      return (
        <div class={smallColumn}>
          {contentTypeProps.store
            .get()
            .items.slice(0, amountToShow)
            .map((result, i) => {
              const params = new URLSearchParams(props.searchProps.sub("params").get() as URLSearchParams);
              params.append("o_p", String(i));
              return (
                <div class="col" key={i}>
                  <CustomPageCardMini customPage={result} params={params} />
                </div>
              );
            })}
        </div>
      );
    case "ACTIONABLE":
      return (
        <div class={bigColumn}>
          {contentTypeProps.store
            .get()
            .items.slice(0, amountToShow)
            .map((result, i) => {
              const params = new URLSearchParams(props.searchProps.sub("params").get() as URLSearchParams);
              params.append("o_p", String(i));
              return (
                <div class="col" key={i}>
                  <ContentCard content={result} layout="horizontal" additionalParams={params} bookmarkHandler={props.bookmarkHandler} ctx={props.ctx} />
                </div>
              );
            })}
        </div>
      );
    case "VERIFAI": {
      return (
        <>
          <div class={bigColumn}>
            {contentTypeProps.store
              .get()
              .items.slice(0, amountToShow)
              .map((result, i) => {
                const params = new URLSearchParams(props.searchProps.sub("params").get() as URLSearchParams);
                params.append("o_p", String(i));
                return (
                  <div class="col" key={i}>
                    <ContentCard content={result} layout="horizontal" additionalParams={params} bookmarkHandler={props.bookmarkHandler} ctx={props.ctx} />
                  </div>
                );
              })}
          </div>
          {props.contentType.sub("paging").sub("totalCount").get() > contentTypeProps.store.sub("items").sub("length").get() && selected ? (
            <div class="d-flex justify-content-center mt-3">
              <GaButton onClick={() => loadMore(props.contentType.sub("paging").sub("page"), props.search)}>
                {props.searchProps.sub("status").get() === "LOADMORE" ? (
                  <div class="spinner-border spinner-border-sm" />
                ) : (
                  props.ctx.t("browse:explore.loadMore", [String(countNextPage(props.contentType.sub("paging").sub("totalCount"), contentTypeProps.store.sub("items").sub("length"), 12))])
                )}
              </GaButton>
            </div>
          ) : null}
        </>
      );
    }
  }
}
